import { l } from '@adornis/base/rxjs-extension/rxjs-helpers.js';
import { trn } from '@adornis/buchungsheld-components/BuchungsheldLitElement.js';
import {
  subscribeProductWaitinglistPriorityRulesForInstitution,
  subscribeProductWaitinglistPriorityRulesForProduct,
} from '@adornis/buchungsheld-db/api/product-waitinglist-priority-rule.js';
import { ProductWaitinglistPriorityRule } from '@adornis/buchungsheld-db/entities/ProductWaitinglistPriorityRule.js';
import type { BHTranslationDictionary } from '@adornis/buchungsheld-db/translations/translation.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import { buildFullOptions } from '@adornis/data-view/client/data-view-options.js';
import '@adornis/data-view/client/x-data-view-table.js';
import '@adornis/data-view/client/x-data-view.js';
import '@adornis/popover/x-tooltip.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { until } from 'lit/directives/until.js';
import { firstValueFrom, switchMap } from 'rxjs';
import { BhPromptAdminManageProductWaitinglistPriorityRule } from '../prompts/bh-prompt-admin-manage-product-waitinglist-priority-rule.js';

/*
 * @element bh-admin-waiting-list-priority-overview
 */
@customElement('bh-admin-waiting-list-priority-overview')
export class BhAdminProductWaitingListPriorityOverview extends ChemistryLitElement {
  @property({ attribute: 'product-id' }) productID = new RXController<string | null>(this, null);
  @property({ attribute: 'institution-id' }) institutionID?: string;

  @state() readonly _translation = new TranslationController<BHTranslationDictionary>(this);

  @state() private productPriorityRules = new RXController(
    this,
    this.productID.observable.pipe(
      switchMap(productID =>
        productID
          ? subscribeProductWaitinglistPriorityRulesForProduct(
              productID,
              true,
            )(ProductWaitinglistPriorityRule.allFields)
          : subscribeProductWaitinglistPriorityRulesForInstitution(this.institutionID!)(
              ProductWaitinglistPriorityRule.allFields,
            ),
      ),
      l('productPriorityRules'),
    ),
  );

  @state() private _newlyCreatedRules: ProductWaitinglistPriorityRule[] = [];
  @state() private _rulesToBeRemoved: ProductWaitinglistPriorityRule[] = [];

  override render() {
    const fullOptions = buildFullOptions({
      class: ProductWaitinglistPriorityRule,
      customColumns: [
        {
          customID: '_type',
          columnName: 'Typ',
          renderItem: priorityRule =>
            this._translation.translate('productWaitinglistPriorityRule', priorityRule._class),
        },
        {
          customID: '_label',
          renderItem: priorityRule =>
            html`
              ${until(
                priorityRule.label().then(label => trn(label)),
                html` <x-text>${this._translation.translate('loading')}...</x-text> `,
              )}
            `,
        },
        /*{
                customID: '_edit',
                columnWidth: 'max-content',
                renderItem: priorityRule =>
                  html`
                    <tr-button-small
                      icon="edit"
                      @click=${() =>
                        BhPromptAdminManageProductPriorityRuleRequirement.showPopup({
                          props: {
                            hasCloseButton: true,
                            mode: 'edit',
                            requirementClass: priorityRule._class,
                            priorityRule,
                            institutionID: this.institutionID,
                          },
                        }).then(() => this.requestUpdate())}
                    >
                      <x-tooltip
                        .text=${html`
                          <x-text> ${this._translation.translate('workshop', 'managePriorityRule')} </x-text>
                        `}
                      ></x-tooltip>
                    </tr-button-small>
                  `,
              }, */
        {
          customID: '_options',
          columnWidth: 'max-content',
          renderItem: data => html`
            <tr-button-small
              icon="delete"
              tone="error"
              .color=${this.colors.tone.error}
              @click=${async () => this.removePriorityRule(data)}
            >
              <x-tooltip
                .text=${html` <x-text> ${this._translation.translate('product', 'removeFromProduct')} </x-text> `}
              ></x-tooltip>
            </tr-button-small>
          `,
        },
      ],
    }).dataViewOptions;

    return html`
      <x-flex space="sm">
        <x-flex horizontal crossaxis-center space-between space="md">
          <x-h3>${this._translation.translate('productWaitinglistPriorityRule', 'plural')}</x-h3>
          <x-button mode="text" icon="add" @click=${async () => this.addPriorityRule()}>
            ${this._translation.translate('product', 'addPriorityRule')}
          </x-button>
        </x-flex>
        <tr-data-view-table
          .loading=${false}
          .data=${(this.productPriorityRules.value ?? [])
            .concat(this._newlyCreatedRules)
            .filter(priorityRule => !this._rulesToBeRemoved.includes(priorityRule))}
          .fullOptions=${fullOptions}
        ></tr-data-view-table>
      </x-flex>
    `;
  }

  private async addPriorityRule() {
    const newPriorityRule =
      await BhPromptAdminManageProductWaitinglistPriorityRule.showPopup<ProductWaitinglistPriorityRule | null>({
        props: {
          hasCloseButton: true,
          mode: 'add',
          institutionID: this.institutionID,
        },
      });

    if (newPriorityRule?.hasValue()) {
      this._newlyCreatedRules = this._newlyCreatedRules.concat([newPriorityRule]);
      this.requestUpdate();
    }
  }

  private async removePriorityRule(requirement: ProductWaitinglistPriorityRule) {
    if (this._newlyCreatedRules.includes(requirement))
      this._newlyCreatedRules = this._newlyCreatedRules.filter(t => t !== requirement);
    else this._rulesToBeRemoved = this._rulesToBeRemoved.concat([requirement]);
  }

  // need to pass product id because can be called on newly created product
  async saveAllPriorityRules(productID?: string) {
    await Promise.all([
      ...(this.productPriorityRules.value ?? []).map(async requirement => {
        if (!(await firstValueFrom(requirement.dirty))) return;
        return requirement.save();
      }),
      ...this._newlyCreatedRules.map(requirement => {
        if (!requirement.productID) requirement.productID = productID;
        if (!requirement.institutionID) requirement.institutionID = this.institutionID!;
        console.log({ requirement });
        return requirement.create();
      }),
      ...this._rulesToBeRemoved.map(async requirement => {
        return requirement.remove();
      }),
    ]);

    this._newlyCreatedRules = [];
    this._rulesToBeRemoved = [];
  }
}
